import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { ManagementFeeDetails } from "../../../interfaces/DTO/mislaka_xml_pension_models";
import { MimshakService } from "../../../services/mimshakService";
import { formatManagementFeePathFeatures } from "../../../utils/enumFormatter";
import { formatCurrency, formatPercentage } from "../../../utils/namesFormater";

interface ManagementFeeDisplayProps {
  feesList: ManagementFeeDetails[];
  label?: string;
  variant?: "h6" | "body2"; // גודל הפונט
  usePercentageFormat?: boolean; // האם להשתמש ב-formatPercentage במקום toFixed(2)
}

/**
 * קומפוננטה להצגת דמי ניהול (בודד או מרובים)
 * מציגה ערכים עם tooltips מלאים, התראות לפי תוקף הטבה, ותמיכה ב-max_fee
 */
export const ManagementFeeDisplay: React.FC<ManagementFeeDisplayProps> = ({
  feesList,
  label,
  variant = "h6",
  usePercentageFormat = false
}) => {
  if (feesList.length === 0) {
    return label ? (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant={variant}>-</Typography>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
      </Box>
    ) : (
      <span></span>
    );
  }

  // מציגים את כל הערכים (בודד או מרובים) עם אותה לוגיקה
  const content = (
    <Box display="flex" flexDirection="column" gap={0.5}>
      {feesList.map((fee, index) => {
        const hasMaxFee = (fee.max_fee_amount ?? 0) > 0;
        const expirationStatus = MimshakService.evaluateFeeBenefitExpiration(fee);

        return (
          <Tooltip
            key={index}
            sx={{ direction: "rtl" }}
            title={
              <>
                {fee.benefit_expiration_date && (
                  <>
                    עודכן לאחרונה: {fee.last_rate_update_date} | תוקף ההטבה:{" "}
                    {fee.benefit_expiration_date}
                    {expirationStatus === "ALERT"
                      ? " - תוקף ההטבה קרוב מאוד!"
                      : expirationStatus === "WARNING"
                      ? " - תוקף ההטבה מסתיים בקרוב!"
                      : ""}
                    <br />
                  </>
                )}
                {!fee.benefit_expiration_date && (
                  <>
                    עודכן לאחרונה: {fee.last_rate_update_date || ""}
                    <br />
                  </>
                )}
                {hasMaxFee && (
                  <>
                    סכום מקסימלי לגבייה:{" "}
                    {fee.max_fee_amount?.toLocaleString()} ₪
                    <br />
                  </>
                )}
                {fee.path_features && (
                  <>{formatManagementFeePathFeatures(fee.path_features)}</>
                )}
              </>
            }
          >
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Typography
                variant={variant}
                sx={{
                  color:
                    expirationStatus === "ALERT"
                      ? "red"
                      : expirationStatus === "WARNING"
                      ? "orange"
                      : "inherit",
                  direction: "ltr",
                  fontSize: variant === "body2" ? "0.875rem" : undefined,
                }}
              >
                {fee.management_fee_rate !== undefined && fee.management_fee_rate !== null
                  ? usePercentageFormat
                    ? formatPercentage(fee.management_fee_rate)
                    : fee.management_fee_rate.toFixed(2)
                  : "-"}
                {hasMaxFee && "*"}
              </Typography>
              {hasMaxFee && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    direction: "ltr",
                    fontSize: variant === "body2" ? "0.65rem" : "0.7rem",
                  }}
                >
                  עד {formatCurrency(fee.max_fee_amount || 0)}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );

  // אם יש label, נציג אותו לצד הערכים
  if (label) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        {content}
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
      </Box>
    );
  }

  // אחרת, רק את הערכים
  return content;
};
